import { ArrowForward } from '@mui/icons-material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Button,
  Grid,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { format } from 'date-fns';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { ignoreGroup } from 'src/layouts/components/utils-method';
import {
  ArrowDownIcon,
  ArrowSeparatorIcon,
  ClipBoardDocIcon,
  DateRangerIcon,
  GoalsIcon,
  UserGroupIcon,
} from 'src/pages/therapist-dashboard/dashboard-icon';
import { getDSNNoteSummary, resetDsnSummary } from 'src/redux/actions/therapist-dashboard-action';
import moment from 'moment';
import DSNSummaryModal from '../dailySessionNotes/DSN summary/DSNSummaryModal';
import { therapyPlanningTestId } from 'src/components/therapyPlanning_2.0/test-ids';

const chipLabel: CSSProperties = {
  fontFamily: 'Inter',
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: '18px',
  letterSpacing: '0.16px',
  paddingRight: '6px',
  paddingLeft: '6px',
  //styleName: components/Chip;
};

const EocDetailModal = ({ activeTab, getCPTcodes }: any) => {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const navigate = useNavigate();
  const { state } = useLocation();
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const getAllCodesResponse = useSelector((state: any) => state.getAllCodeReducers);
  const planOfCareResponse = useSelector((state: any) => state?.getPlanOfCareReducer);
  const episodeOfCareData = useSelector((state: any) => state?.getSingleEpisodeOfCare?.response);
  const isTherapyPlanningPatient = useSelector(
    (state: any) => state?.getIsTherapyPlanningPatientReducer?.response?.data
  );
  const planOfCareDetails = useSelector(
    (state: any) => state?.getPlanOfCareReducer?.response?.data
  );

  const planOfCareDetailsLoading = useSelector(
    (state: any) => state?.getPlanOfCareReducer?.loading
  );

  const dispatch = useDispatch();
  const dsnSummary = useSelector((state: any) => state.getDsnSummaryReducer);
  const [openSummary, setOpenSummary] = useState(false);

  const handleOpenDsnSummary = () => {
    const endDate =
      activeTab === 'PT'
        ? planOfCareDetails?.ptCertEndDate
        : activeTab === 'OT'
        ? planOfCareDetails?.otCertEndDate
        : planOfCareDetails?.slpCertEndDate;

    dispatch(resetDsnSummary());
    dispatch(
      getDSNNoteSummary({
        patientId: patientData?.uuid,
        type: 'dashboard',
        params: {
          discipline: activeTab,
          localDate: moment(endDate).format('YYYY-MM-DD'),
        },
      })
    );
    setOpenSummary(true);
  };

  useEffect(() => {
    const checkOverflow = () => {
      if (boxRef.current && !isExpanded) {
        setShowReadMore(boxRef.current?.scrollHeight > boxRef.current?.clientHeight);
      }
    };

    checkOverflow();

    window.addEventListener('resize', checkOverflow);

    return () => window.removeEventListener('resize', checkOverflow);
  }, [planOfCareDetails, activeTab]);

  const handleToggleExpand = () => {
    setIsExpanded((prevState) => !prevState);
    // setShowReadMore(true);
  };

  const RenderGoals = (discipline: 'PT' | 'OT' | 'SLP') => {
    return (
      <Box mb={2}>
        {!planOfCareDetails?.goalSkills?.[discipline] ? (
          <Typography
            variant="body2"
            color={'rgba(108, 115, 127, 1)'}
          >
            {' '}
            -{' '}
          </Typography>
        ) : (
          <Stack rowGap={1}>
            <Box
              display={'flex'}
              gap={0.5}
              textOverflow={'ellipsis'}
              overflow={'hidden'}
              whiteSpace={'wrap'}
            >
              <Typography
                variant="body2"
                color={'rgba(108, 115, 127, 1)'}
              >
                STG
              </Typography>
              <Typography
                variant="body2"
                sx={{ wordBreak: 'break-word' }}
              >
                |{' '}
                {planOfCareDetails?.goalSkills?.[discipline]?.['SHORT_TERM']?.length ? (
                  planOfCareDetails?.goalSkills[discipline]['SHORT_TERM']?.length === 1 ? (
                    <span>{planOfCareDetails?.goalSkills[discipline]['SHORT_TERM'][0]}.</span>
                  ) : (
                    <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                      {planOfCareDetails?.goalSkills[discipline]['SHORT_TERM']?.map(
                        (goal: string, index: number) => <li key={index}>{goal}.</li>
                      )}
                    </ul>
                  )
                ) : (
                  <>N/A</>
                )}
              </Typography>
            </Box>

            <Box
              display={'flex'}
              gap={0.5}
            >
              <Typography
                variant="body2"
                color={'rgba(108, 115, 127, 1)'}
              >
                LTG
              </Typography>

              <Typography
                variant="body2"
                sx={{ wordBreak: 'break-word' }}
              >
                |{' '}
                {planOfCareDetails?.goalSkills?.[discipline]?.['LONG_TERM']?.length ? (
                  planOfCareDetails?.goalSkills[discipline]['LONG_TERM']?.length === 1 ? (
                    <span>{planOfCareDetails?.goalSkills[discipline]['LONG_TERM'][0]}.</span>
                  ) : (
                    <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                      {planOfCareDetails?.goalSkills[discipline]['LONG_TERM']?.map(
                        (goal: string, index: number) => <li key={index}>{goal}.</li>
                      )}
                    </ul>
                  )
                ) : (
                  <>N/A</>
                )}
              </Typography>
            </Box>
          </Stack>
        )}
      </Box>
    );
  };

  return planOfCareDetailsLoading ? (
    <Table sx={{ minWidth: 700 }}>
      <TableBody>
        <TableRow>
          <TableCell colSpan={8}>
            <Box
              textAlign="center"
              p={3}
            >
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </Box>
          </TableCell>
          <TableCell colSpan={8}>
            <Box
              textAlign="center"
              p={3}
            >
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </Box>
          </TableCell>
          <TableCell colSpan={8}>
            <Box
              textAlign="center"
              p={3}
            >
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </Box>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  ) : (
    <>
      <Grid
        sx={{
          display: 'grid',

          gridTemplateColumns: {
            md: '1fr',
            lg: '450px 350px auto',
            xl: '450px 380px auto',
          },
        }}
        rowGap={3}
        overflow={'auto'}
        ml={3}
        mt={3}
      >
        <Grid
          flexDirection={'column'}
          display={'flex'}
          gap={2}
        >
          <Grid
            flexDirection={'row'}
            display={'flex'}
            gap={1}
          >
            <Box
              sx={{
                width: '44px',
                height: '44px',
                padding: '10px 0px 0px 0px',
                textAlign: 'center',
                gap: '10px',
                borderRadius: '12px',
                opacity: '0px',
                background: 'rgba(245, 247, 255, 1)',
              }}
            >
              <ArrowSeparatorIcon />
            </Box>
            <Box
              display={'flex'}
              flexDirection={'column'}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: '21.58px',
                  textAlign: 'left',
                  textUnderlinePosition: 'from-font',
                  textDecorationSkipInk: 'none',
                  color: 'rgba(108, 115, 127, 1)',
                }}
                mb={1}
              >
                Treatment Codes from most recent {activeTab} evaluation
              </Typography>

              {getCPTcodes?.length
                ? getCPTcodes?.map((code: string, index: number) => {
                    return (
                      <Box
                        key={index}
                        sx={{
                          width: 'fit-content',
                          borderRadius: '12px',
                          border: '1px solid rgba(0, 0, 0, 0.26)',
                          padding: '3px 4px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          marginBottom: '10px',
                        }}
                      >
                        <Typography sx={chipLabel}>
                          {`${code} ${
                            getAllCodesResponse?.response?.data?.cptCodesDescription?.[
                              code?.includes('(Group)') ? ignoreGroup(code) : code
                            ]
                              ? ` | ${getAllCodesResponse?.response?.data?.cptCodesDescription?.[
                                  code?.includes('(Group)') ? ignoreGroup(code) : code
                                ]}`
                              : ''
                          } `}
                        </Typography>
                      </Box>
                    );
                  })
                : '-'}
            </Box>
          </Grid>
        </Grid>
        <Grid
          flexDirection={'column'}
          display={'flex'}
          gap={3}
        >
          <Grid
            flexDirection={'row'}
            display={'flex'}
            gap={1}
          >
            <Box
              sx={{
                width: '44px',
                height: '44px',
                padding: '10px 0px 0px 0px',
                textAlign: 'center',
                gap: '10px',
                borderRadius: '12px',
                opacity: '0px',
                background: 'rgba(245, 247, 255, 1)',
              }}
            >
              <ClipBoardDocIcon />
            </Box>
            <Box
              display={'flex'}
              flexDirection={'column'}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: '21.58px',
                  textAlign: 'left',
                  textUnderlinePosition: 'from-font',
                  textDecorationSkipInk: 'none',
                  color: 'rgba(108, 115, 127, 1)',
                }}
              >
                Certifications
              </Typography>

              <Box>
                <Stack
                  flexDirection="row"
                  justifyContent="space-between"
                  py={1}
                  gap={1}
                >
                  <Stack
                    gap="1rem"
                    flexDirection="row"
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: '#6C737F' }}
                    >
                      Start Date{' '}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      noWrap
                    >
                      {activeTab === 'PT'
                        ? episodeOfCareData?.ptCertStartDate
                          ? format(
                              new Date(
                                new Date(episodeOfCareData?.ptCertStartDate).valueOf() +
                                  new Date(episodeOfCareData?.ptCertStartDate).getTimezoneOffset() *
                                    60 *
                                    1000
                              ),
                              'MMM dd, yyyy'
                            )
                          : '-'
                        : activeTab === 'OT'
                        ? episodeOfCareData?.otCertStartDate
                          ? format(
                              new Date(
                                new Date(episodeOfCareData?.otCertStartDate).valueOf() +
                                  new Date(episodeOfCareData?.otCertStartDate).getTimezoneOffset() *
                                    60 *
                                    1000
                              ),
                              'MMM dd, yyyy'
                            )
                          : '-'
                        : episodeOfCareData?.slpCertStartDate
                        ? format(
                            new Date(
                              new Date(episodeOfCareData?.slpCertStartDate).valueOf() +
                                new Date(episodeOfCareData?.slpCertStartDate).getTimezoneOffset() *
                                  60 *
                                  1000
                            ),
                            'MMM dd, yyyy'
                          )
                        : '-'}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack
                  flexDirection="row"
                  justifyContent="space-between"
                  py={1}
                  gap={1}
                >
                  <Stack
                    gap="1rem"
                    flexDirection="row"
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: '#6C737F' }}
                    >
                      End Date{' '}
                    </Typography>

                    <Typography
                      variant="subtitle2"
                      noWrap
                    >
                      {activeTab === 'PT'
                        ? episodeOfCareData?.ptCertEndDate
                          ? format(
                              new Date(
                                new Date(episodeOfCareData?.ptCertEndDate).valueOf() +
                                  new Date(episodeOfCareData?.ptCertEndDate).getTimezoneOffset() *
                                    60 *
                                    1000
                              ),
                              'MMM dd, yyyy'
                            )
                          : '-'
                        : activeTab === 'OT'
                        ? episodeOfCareData?.otCertEndDate
                          ? format(
                              new Date(
                                new Date(episodeOfCareData?.otCertEndDate).valueOf() +
                                  new Date(episodeOfCareData?.otCertEndDate).getTimezoneOffset() *
                                    60 *
                                    1000
                              ),
                              'MMM dd, yyyy'
                            )
                          : '-'
                        : episodeOfCareData?.slpCertEndDate
                        ? format(
                            new Date(
                              new Date(episodeOfCareData?.slpCertEndDate).valueOf() +
                                new Date(episodeOfCareData?.slpCertEndDate).getTimezoneOffset() *
                                  60 *
                                  1000
                            ),
                            'MMM dd, yyyy'
                          )
                        : '-'}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Box>
          </Grid>
          <Grid
            flexDirection={'row'}
            display={'flex'}
            gap={1}
          >
            <Box
              sx={{
                width: '44px',
                height: '44px',
                padding: '10px 0px 0px 0px',
                textAlign: 'center',
                gap: '10px',
                borderRadius: '12px',
                opacity: '0px',
                background: 'rgba(245, 247, 255, 1)',
              }}
            >
              <DateRangerIcon />
            </Box>
            <Box
              display={'flex'}
              flexDirection={'column'}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: '21.58px',
                  textAlign: 'left',
                  textUnderlinePosition: 'from-font',
                  textDecorationSkipInk: 'none',
                  color: 'rgba(108, 115, 127, 1)',
                }}
              >
                Plan Summary
              </Typography>
              <Box>
                <Stack
                  flexDirection="row"
                  justifyContent="space-between"
                  py={1}
                  gap={1}
                >
                  <Stack
                    gap="1rem"
                    flexDirection="row"
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: '#6C737F' }}
                    >
                      Duration
                    </Typography>

                    <Typography
                      variant="subtitle2"
                      noWrap
                    >
                      {planOfCareDetails?.durationMap[activeTab] || '-'}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack
                  flexDirection="row"
                  justifyContent="space-between"
                  py={1}
                  gap={1}
                >
                  <Stack
                    gap="1rem"
                    flexDirection="row"
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: '#6C737F' }}
                    >
                      Frequency
                    </Typography>

                    <Typography
                      variant="subtitle2"
                      noWrap
                    >
                      {planOfCareDetails?.disciplineMap[activeTab] || '-'}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack
                  flexDirection="row"
                  justifyContent="space-between"
                  py={1}
                  gap={1}
                >
                  <Stack
                    gap="1rem"
                    flexDirection="row"
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: '#6C737F' }}
                    >
                      Treatment time
                    </Typography>

                    <Typography
                      variant="subtitle2"
                      noWrap
                    >
                      {planOfCareDetails?.treatmentTime || '-'}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack>
                  <Button
                    variant="text"
                    endIcon={<ArrowForward />}
                    disabled={!isTherapyPlanningPatient || episodeOfCareData?.status === 'CLOSED'}
                    sx={{
                      color: '#6366F1',
                      padding: '0px',
                      paddingBottom: '2px',
                      cursor: 'pointer',
                      borderRadius: 'unset',
                      backgroundColor: 'transparent',
                      width: 'fit-content',
                    }}
                    onClick={() => {
                      navigate('/scheduling/therapy-planning/details/info', {
                        state: {
                          patientId: state?.getPatientId,
                          searchData: patientData?.name,
                        },
                      });
                    }}
                    data-testid={therapyPlanningTestId('plan-therapy')}
                  >
                    Plan Therapy
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Grid>
        </Grid>
        {/* treatment mode */}
        <Grid
          flexDirection={'column'}
          display={'flex'}
          gap={3}
        >
          <Grid
            flexDirection={'row'}
            display={'flex'}
            gap={1}
          >
            <Box
              sx={{
                width: '44px',
                height: '44px',
                padding: '10px 0px 0px 0px',
                textAlign: 'center',
                gap: '10px',
                borderRadius: '12px',
                opacity: '0px',
                background: 'rgba(245, 247, 255, 1)',
              }}
            >
              <UserGroupIcon />
            </Box>
            <Box
              display={'flex'}
              flexDirection={'column'}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: '21.58px',
                  textAlign: 'left',
                  textUnderlinePosition: 'from-font',
                  textDecorationSkipInk: 'none',
                  color: 'rgba(108, 115, 127, 1)',
                }}
                mb={1}
              >
                Treatment modes
              </Typography>

              <Stack gap=".5rem">
                <Stack
                  gap="1rem"
                  flexDirection="row"
                >
                  <Typography
                    variant="body2"
                    sx={{ color: '#6C737F' }}
                  >
                    Group:{' '}
                  </Typography>
                  <Typography variant="subtitle2">
                    {planOfCareDetails?.allowGroupTherapy ? 'Allowed' : 'Not Allowed'}
                  </Typography>
                </Stack>
                <Stack
                  gap=".5rem"
                  flexDirection="row"
                >
                  <Typography
                    variant="body2"
                    sx={{ color: '#6C737F' }}
                  >
                    Concurrent:{' '}
                  </Typography>
                  <Typography variant="subtitle2">
                    {planOfCareDetails?.allowConcurrentTherapy ? 'Allowed' : 'Not Allowed'}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Grid>
          {/* Therapy Overview */}
          <Grid
            flexDirection={'row'}
            display={'flex'}
            gap={1}
          >
            <Box
              sx={{
                width: '44px',
                height: '44px',
                padding: '10px 0px 0px 0px',
                textAlign: 'center',
                gap: '10px',
                borderRadius: '12px',
                opacity: '0px',
                background: 'rgba(245, 247, 255, 1)',
              }}
            >
              <ClipBoardDocIcon />
            </Box>
            <Box
              display={'flex'}
              flexDirection={'column'}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: '21.58px',
                  textAlign: 'left',
                  textUnderlinePosition: 'from-font',
                  textDecorationSkipInk: 'none',
                  color: 'rgba(108, 115, 127, 1)',
                }}
                mb={1}
              >
                Therapy Overview
              </Typography>
              <Stack>
                <Button
                  disabled={
                    activeTab === 'PT'
                      ? !planOfCareDetails?.checkPtActive
                      : activeTab === 'OT'
                      ? !planOfCareDetails?.checkOtActive
                      : !planOfCareDetails?.checkSlpActive
                  }
                  sx={{
                    color: '#6366F1',
                    padding: '0px',
                    paddingBottom: '2px',
                    cursor: 'pointer',
                    borderRadius: 'unset',
                    backgroundColor: 'transparent',
                    width: 'fit-content',
                    fontSize: '0.85rem',
                  }}
                  onClick={handleOpenDsnSummary}
                >
                  View DSN Summary
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>
        <Grid>
          <Grid
            flexDirection={'row'}
            display={'flex'}
            gap={1}
          ></Grid>
        </Grid>
      </Grid>
      <Grid
        flexDirection={'row'}
        display={'flex'}
        gap={1}
        rowGap={3}
        overflow={'auto'}
        ml={3}
        mt={3}
      >
        <Box
          sx={{
            width: '44px',
            height: '44px',
            padding: '15px 20px 35px 15px',
            textAlign: 'center',
            gap: '10px',
            borderRadius: '12px',
            opacity: '0px',
            background: 'rgba(245, 247, 255, 1)',
            mr: 2,
          }}
        >
          <GoalsIcon />
        </Box>

        <Box
          display={'flex'}
          flexDirection={'column'}
        >
          <Stack
            display={'flex'}
            flexDirection={'column'}
          >
            <Grid>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: '21.58px',
                  textAlign: 'left',
                  textUnderlinePosition: 'from-font',
                  textDecorationSkipInk: 'none',
                  color: 'rgba(108, 115, 127, 1)',
                }}
                mb={1}
              >
                Goals
              </Typography>
            </Grid>

            <Grid
              ref={boxRef}
              height={isExpanded ? 'auto' : '230px'}
              overflow="hidden"
              flexWrap={'nowrap'}
              textOverflow={'ellipsis'}
              whiteSpace={'wrap'}
            >
              {RenderGoals(activeTab)}
            </Grid>
          </Stack>

          {showReadMore && (
            <Stack>
              <Button
                endIcon={isExpanded ? <KeyboardArrowUpIcon /> : <ArrowDownIcon />}
                sx={{
                  color: '#111927',
                  padding: '0px',
                  paddingBottom: '2px',
                  cursor: 'pointer',
                  borderRadius: 'unset',
                  backgroundColor: 'transparent',
                  width: 'fit-content',
                }}
                onClick={handleToggleExpand}
              >
                {isExpanded ? 'Read less' : 'Read more'}
              </Button>
            </Stack>
          )}
        </Box>
      </Grid>
      <DSNSummaryModal
        open={openSummary}
        title={`DSN Summary | ${activeTab} | Last 7 Sessions in This EOC`}
        onClose={() => setOpenSummary(false)}
        data={dsnSummary?.response}
      />
    </>
  );
};

export default EocDetailModal;
